import { circularApiTypes } from "@api/circularApiTypes";

/**
 * Namespace for the Circular component.
 */
export namespace CircularTypes {
    /**
     * The URL query string parameters.
     */
    export interface UrlParams {
        /**
         * The circular ID.
         */
        circularId: string | undefined;
        /**
         * The store ID.
         */
        storeId: string | undefined;
        /**
         * The user ID.
         */
        udid: string | undefined;
        /**
         * The app name.
         */
        app: string | undefined;
        /**
         * The platform the circular was loaded on.
         */
        plt: string | undefined;
        /**
         * If true, the circular was loaded in-app.
         * If false, the circular was loaded out-of-app.
         */
        inApp: boolean;
        /**
         * If true, the circular is being loaded in "preview" mode.
         * This mode will not send GA requests or AA payload/event requests.
         */
        preview?: boolean;
    }

    /**
     * The definition of a Detailed List Item.
     */
    export interface DetailedListItem {
        /**
         * The barcode of the product.
         */
        product_barcode: string;
        /**
         * The brand of the product.
         */
        product_brand: string;
        /**
         * The category of the product.
         */
        product_category: string;
        /**
         * The discount given for the product.
         */
        product_discount: string;
        /**
         * The image used for display of the product.
         */
        product_image: string;
        /**
         * The SKU of the product.
         */
        product_sku: string;
        /**
         * The name/title of the product.
         */
        product_title: string;
    }

    /**
     * Defines the data structure of an item.
     */
    export interface ItemDefinition {
        /**
         * The image URL.
         */
        imageUrl: string;
        /**
         * The item title.
         */
        title: string;
        /**
         * Additional details about the item.
         */
        additionalDetails?: string;
        /**
         * The item price.
         */
        price: circularApiTypes.models.MoneyInput | null;
        /**
         * If true, the item gets a while supplies last message.
         */
        isWhileSuppliesLast?: boolean;
        /**
         * The tracking ID of the item.
         */
        trackingId: string;
        /**
         * The product brand.
         */
        brand: string;
        /**
         * The product category.
         */
        category: string;
        /**
         * The product UPC.
         */
        upc: string;
        /**
         * The retailer's SKU for the product.
         */
        sku: string;
        /**
         * The URL used when an items "add" button is clicked when the circular is loaded out-of-network.
         */
        outOfNetworkActionUrl?: string;
    }

    /**
     * Interface defining inputs to the console.warn that is called when the
     * addItemToList call is made from the definition used when the window.AdAdapted
     * object wasn't available.
     */
    export interface AddItemToListConsoleWarnInputs {
        /**
         * The circular ID.
         */
        circularId: string;
        /**
         * The circular tracking ID.
         */
        trackingId: string;
        /**
         * The circular title.
         */
        title: string;
        /**
         * The item brand.
         */
        brand: string;
        /**
         * The item category.
         */
        category: string;
        /**
         * The item UPC.
         */
        upc: string;
        /**
         * The item SKU.
         */
        retailerSku: string;
        /**
         * The item price.
         */
        discount: string;
        /**
         * The item image.
         */
        image: string;
    }

    /**
     * All possible event names that can be triggered.
     */
    export enum EventName {
        /**
         * Event name for adding an item to list.
         */
        ADDED_TO_LIST = "added_to_list",
        /**
         * Event name for viewing the circular.
         */
        VIEWED = "viewed",
        /**
         * Event name for queueing and item with the payload scheduler.
         */
        ITEM_QUEUED = "item_queued",
        /**
         * Event name for out-of-network referrals.
         */
        REFERRAL = "referral",
    }
}
